import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, FileText, Trash2, Play, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import AddTemplateDialog from './AddTemplateDialog';
import ApplyTemplateDialog from './ApplyTemplateDialog';

// Green theme tokens
const THEME = {
  primary: '#86EFAC',
  light: '#DCFCE7',
  lighter: '#F0FDF4',
  dark: '#166534',
  icon: '#22C55E',
  border: '#D1FAE5',
  surface: '#FAFDF7',
};

const TemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await API.get('/shopping/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this template? You can always create it again.')) return;
    try {
      await API.delete(`/shopping/templates/${id}`);
      toast.success('Template removed');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to remove template');
    }
  };

  const handleApply = (template) => {
    setSelectedTemplate(template);
    setApplyDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <>
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
        <div>
          <h3 
            className="text-xl font-semibold"
            style={{ color: THEME.dark }}
          >
            Templates
          </h3>
          <p className="text-sm text-gray-500 mt-1">Reusable shopping lists</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="w-full sm:w-auto shadow-sm hover:shadow-md transition-shadow"
          style={{ 
            backgroundColor: THEME.primary,
            color: THEME.dark,
          }}
          data-testid="add-template-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <div 
          className="text-center py-16 rounded-2xl border-2 border-dashed"
          style={{ 
            backgroundColor: THEME.lighter,
            borderColor: THEME.border,
          }}
        >
          <div 
            className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4"
            style={{ backgroundColor: THEME.light }}
          >
            <FileText className="h-8 w-8" style={{ color: THEME.dark }} />
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">Save Time with Templates</h4>
          <p className="text-gray-500 mb-6 max-w-sm mx-auto">
            Create reusable shopping lists for groceries, household items, or any regular purchases
          </p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="shadow-sm hover:shadow-md transition-shadow"
            style={{ 
              backgroundColor: THEME.primary,
              color: THEME.dark,
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Your First Template
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {templates.map((template) => (
            <Card 
              key={template.id} 
              className="overflow-hidden hover:shadow-lg transition-all duration-200"
              style={{ 
                borderColor: THEME.border,
                borderWidth: '1px',
                borderLeftWidth: '4px',
                borderLeftColor: THEME.primary,
              }}
            >
              <CardHeader className="pb-3 pt-5 px-5">
                <CardTitle className="text-base font-semibold flex items-center gap-2 text-gray-900">
                  <div 
                    className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: THEME.light }}
                  >
                    <FileText className="h-4 w-4" style={{ color: THEME.dark }} />
                  </div>
                  <span className="truncate">{template.name}</span>
                </CardTitle>
                {template.description && (
                  <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">{template.description}</p>
                )}
              </CardHeader>
              <CardContent className="px-5 pb-5">
                <div className="mb-4">
                  <Badge 
                    variant="outline" 
                    className="text-xs font-medium"
                    style={{ 
                      borderColor: THEME.border,
                      color: THEME.dark,
                    }}
                  >
                    <ShoppingBag className="h-3 w-3 mr-1.5" />
                    {template.items?.length || 0} items
                  </Badge>
                </div>
                
                {template.items && template.items.length > 0 && (
                  <div className="space-y-1.5 mb-4">
                    {template.items.slice(0, 3).map((item, idx) => (
                      <div 
                        key={idx} 
                        className="text-xs p-2 rounded-lg"
                        style={{ backgroundColor: THEME.lighter, color: '#4B5563' }}
                      >
                        {item.name}
                      </div>
                    ))}
                    {template.items.length > 3 && (
                      <p className="text-xs text-gray-400 pl-2">
                        +{template.items.length - 3} more
                      </p>
                    )}
                  </div>
                )}
                
                {/* Action buttons */}
                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <Button
                    onClick={() => handleApply(template)}
                    className="flex-1 shadow-sm hover:shadow-md transition-shadow"
                    style={{ 
                      backgroundColor: THEME.primary,
                      color: THEME.dark,
                    }}
                    size="sm"
                    data-testid={`apply-template-${template.id}`}
                  >
                    <Play className="h-4 w-4 mr-1.5" />
                    Use Template
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(template.id)}
                    className="text-rose-400 hover:text-rose-600 hover:bg-rose-50"
                    data-testid={`delete-template-${template.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AddTemplateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={() => {
          fetchTemplates();
          setDialogOpen(false);
        }}
      />
      
      <ApplyTemplateDialog
        open={applyDialogOpen}
        onOpenChange={setApplyDialogOpen}
        template={selectedTemplate}
        onSuccess={() => {
          setApplyDialogOpen(false);
          setSelectedTemplate(null);
        }}
      />
    </>
  );
};

export default TemplatesTab;
